import React, { Component } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import Board from 'react-trello';
import includes from 'lodash/includes';
import capitalize from 'lodash/capitalize';
import toLower from 'lodash/toLower';
import Auth from '@aws-amplify/auth';
import { Segment } from '@fxtr/i18n';
import { message } from 'antd';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import DashboardCard from './DashboardCard';
import DashboardHeader from './Header';
import detailedStatus from './detailedStatus';
import { getDashboardBookings } from './api';
import applyStatusFilter from '$app/shared/components/StatusSelectFilter/filterLogic';
import { getSelectedStatusValue } from '$app/shared/components/StatusSelectFilter/filterOptions';
import { getSavedFiltersFromLocalStorage, saveFiltersInLocalStorage } from '../../../../../common/helpers';

const laneTitles = {
  collection: 'Collection',
  fixing: 'Fixing',
  delivery: 'Delivery',
  review: 'To Review',
  approval: 'Customer Approval',
  garageAllocation: 'Garage allocation',
  garageProposeNewTime: 'Garage propose new time',
  driverToAllocate: 'Driver to allocate',
};

const DASHBOARD_FILTERS = 'DASHBOARD_FILTERS';

const sortCardsPerLane = (type, a, b) => {
  if (type === 'driverToAllocate') {
    return a.booking.collectionDateTime > b.booking.collectionDateTime ? 1 : -1; // driverToAllocate
  }

  if (a.booking.detailedStatus && b.booking.detailedStatus) {
    if (a.booking.detailedStatus.priority > b.booking.detailedStatus.priority) return -1;
    if (a.booking.detailedStatus.priority < b.booking.detailedStatus.priority) return 1;
  }

  if (type === 'collection')
    return a.booking.collectionDateTimeLocal > b.booking.collectionDateTimeLocal ? 1 : -1;

  if (includes(['fixing', 'review', 'approval'], type))
    return a.booking.deliveryDateTimeLocal > b.booking.deliveryDateTimeLocal ? 1 : -1;

  if (type === 'delivery') return a.booking.deliveryDateTimeLocal > b.booking.deliveryDateTimeLocal ? 1 : -1;

  return a.booking.collectionDateTime > b.booking.collectionDateTime ? 1 : -1; // garageAllocation
};

const saveFilter = (filter, value) => {
  const savedFilters = getSavedFiltersFromLocalStorage(DASHBOARD_FILTERS);
  saveFiltersInLocalStorage(DASHBOARD_FILTERS, {
    ...savedFilters,
    [filter]: value,
  });
};

const initialState = {
  authUser: null,
  dashboardData: null,
  searchFieldValue: null,
  caseManagerId: 'currentUser',
  garageBusinessName: null,
  selectedCndType: 'allCndTypes',
  selectedStatus: 'none',
  selectedDateRange: [],
  segment: getSavedFiltersFromLocalStorage(DASHBOARD_FILTERS).segment || 'all',
  segments: ['all', Segment.Fixter_UK, Segment.Fixter_FR],
};

class Dashboard extends Component {
  state = initialState;

  async componentDidMount() {
    try {
      await Promise.all([this.loadAuthUser(), this.loadDashboardData()]);
      this.poller = window.setInterval(this.loadDashboardData, 20000);
    } catch (error) {
      message.error(error.message || 'Could not load authenticated user');
    }
  }

  componentWillUnmount() {
    if (this.poller) {
      window.clearInterval(this.poller);
    }
  }

  getViewVariables = () => {
    const { template } = this.props;
    return {
      isDashboardView: template === 'dashboard',
      isAllocationView: template === 'allocations',
      startDate: moment().subtract(7, 'day').startOf('day').format(),
      endDate: moment().add(7, 'day').startOf('day').format(),
      endOfToday: moment().endOf('day').format(),
    };
  };

  loadAuthUser = async () => {
    const amplifyUser = await Auth.currentAuthenticatedUser({ bypassCache: false });
    const authUser = {
      username: amplifyUser.username,
      email: amplifyUser.attributes?.email,
      name: amplifyUser.attributes?.given_name,
      surname: amplifyUser.attributes?.family_name,
    };
    this.setState((state) => ({ ...state, authUser }));
  };

  loadDashboardData = async () => {
    try {
      const data = await getDashboardBookings(this.getViewVariables());
      this.setState((state) => ({ ...state, dashboardData: data }));
    } catch (error) {
      message.error(error.message || 'Could not load dashboard data');
    }
  };

  onSegmentSelect = (segment) => {
    this.setState((state) => ({ ...state, segment }));
    saveFilter('segment', segment);
  };

  onCndTypeSelect = (selectedCndType) => {
    this.setState((state) => ({ ...state, selectedCndType }));
  };

  onStatusSelect = (selectedStatusId) => {
    this.setState((state) => ({ ...state, selectedStatus: getSelectedStatusValue(selectedStatusId) }));
  };

  onChangeSearch = (e) => {
    e.preventDefault();
    const searchFieldValue = toLower(e.target.value);
    this.setState((state) => ({ ...state, searchFieldValue }));
  };

  onSelectGarage = (garageBusinessName) => {
    this.setState((state) => ({ ...state, garageBusinessName: toLower(garageBusinessName) }));
  };

  onCaseManagerChange = (value) => {
    const { authUser } = this.state;

    if (value === 'currentUser') {
      this.setState((state) => ({ ...state, caseManagerId: authUser.username }));
    } else if (value === 'allCustomers') {
      this.setState((state) => ({ ...state, caseManagerId: 'allCustomers' }));
    } else {
      this.setState((state) => ({ ...state, caseManagerId: value }));
    }
  };

  onDateRangeChange = (selectedDateRange) => {
    this.setState((state) => ({ ...state, selectedDateRange }));
  };

  clearAll = () => {
    localStorage.removeItem(DASHBOARD_FILTERS);
    this.setState((state) => ({ ...initialState, authUser: state.authUser, dashboardData: state.dashboardData }));
  };

  dateRangeFilterLogic = (booking, dateRange) => {
    if (!dateRange.length) return true;

    const filterStartDate = moment(dateRange[0]);
    const filterEndDate = moment(dateRange[1]);

    const collectionDate = moment(booking.collectionDateTimeLocal);
    const deliveryDate = moment(booking.collectionDateTimeLocal);

    const collectionDateInRange = moment(collectionDate).isBetween(
      filterStartDate,
      filterEndDate,
      'day',
      '[]'
    );
    const deliveryDateInRange = moment(deliveryDate).isBetween(filterStartDate, filterEndDate, 'day', '[]');

    return collectionDateInRange || deliveryDateInRange;
  };

  render() {
    const { template } = this.props;

    const {
      authUser,
      searchFieldValue,
      caseManagerId,
      garageBusinessName,
      segment,
      selectedCndType,
      selectedStatus,
      selectedDateRange,
    } = this.state;

    const { dashboardData } = this.state;
    if (!authUser || !dashboardData) return <Loading />;

    const isDashboardView = template === 'dashboard';
    const isAllocationView = template === 'allocations';

    document.title = this.props.template === 'dashboard' ? 'Dashboard' : 'Allocations';

    return (
      <Container>
        {(() => {
            const data = dashboardData;
            const getLane = (type, bookings) => {
              const cards = bookings
                .map((booking) => ({ detailedStatus: detailedStatus(booking), ...booking }))
                .map((booking) => ({
                  template,
                  booking,
                  drivers: data.drivers,
                  caseManagers: data.caseManagers,
                  currentUser: authUser,
                  id: `${booking.bookingId} `,
                  draggable: false,
                }))
                .filter(({ booking }) => {
                  if (type === 'driverToAllocate') {
                    const trip = booking?.trips?.find(
                      (tr) => tr.status === 'active' && tr.allocationStatus !== 'accepted'
                    );
                    return !!trip;
                  }
                  return true;
                })
                .sort((a, b) => sortCardsPerLane(type, a, b));

              return {
                id: type,
                title: laneTitles[type],
                label: String(cards.length),
                cards,
              };
            };

            const segmentFilter = (b) => {
              return segment === 'all'
                ? [Segment.Fixter_FR, Segment.Fixter_UK].includes(b.segment)
                : segment === b.segment;
            };

            const caseManagerFilter = (b) => {
              // bookings assigned to the logged in case manager + the unassigned bookings
              if (caseManagerId === 'currentUser' || caseManagerId === authUser.username) {
                return !b.caseManagerId || authUser.username === b.caseManagerId;
              }
              // bookings assigned to a specific case manager
              if (caseManagerId !== 'currentUser' && caseManagerId !== 'allCustomers') {
                return caseManagerId === b.caseManagerId;
              }
              // all bookings
              return true;
            };

            const applyFilter = (bookings) =>
              bookings.filter(
                (b) =>
                  segmentFilter(b) &&
                  (searchFieldValue ? toLower(JSON.stringify(b)).includes(searchFieldValue) : true) &&
                  caseManagerFilter(b) &&
                  (garageBusinessName ? toLower(JSON.stringify(b)).includes(garageBusinessName) : true) &&
                  (selectedCndType === 'allCndTypes' || b.collectionDeliveryOption === selectedCndType) &&
                  applyStatusFilter(b, selectedStatus) &&
                  this.dateRangeFilterLogic(b, selectedDateRange)
              );

            const columns = [
              isDashboardView && 'collection',
              isDashboardView && 'fixing',
              isDashboardView && 'review',
              isDashboardView && 'approval',
              isDashboardView && 'delivery',
              isAllocationView && 'garageAllocation',
              isAllocationView && 'garageProposeNewTime',
              isAllocationView && 'driverToAllocate',
            ].filter(Boolean);

            const lanes = columns.reduce((ac, i) => [...ac, getLane(i, applyFilter(data[i]))], []);

            const bookings = columns.reduce((ac, i) => [...ac, ...applyFilter(data[i])], []);

            return (
              <div>
                <DashboardHeader
                  segment={segment}
                  segments={this.state.segments}
                  onSegmentSelect={this.onSegmentSelect}
                  selectedCndType={this.state.selectedCndType}
                  onCndTypeSelect={this.onCndTypeSelect}
                  selectedStatus={this.state.selectedStatus}
                  onStatusSelect={this.onStatusSelect}
                  searchFieldValue={this.state.searchFieldValue}
                  onChangeSearch={this.onChangeSearch}
                  garageBusinessName={this.state.garageBusinessName}
                  onSelectGarage={this.onSelectGarage}
                  caseManagers={data.caseManagers}
                  caseManagerId={this.state.caseManagerId}
                  onCaseManagerChange={this.onCaseManagerChange}
                  selectedDateRange={this.state.selectedDateRange}
                  onDateRangeChange={this.onDateRangeChange}
                  clearAll={this.clearAll}
                  title={capitalize(template)}
                  bookings={bookings}
                />
                <div className="dashboard-grid">
                  <Board
                    data={{ lanes }}
                    components={{
                      Card: DashboardCard,
                    }}
                  />
                </div>
              </div>
            );
        })()}
      </Container>
    );
  }
}

Dashboard.propTypes = {
  template: PropTypes.oneOf(['dashboard', 'allocations']).isRequired,
};

export default withRouter(Dashboard);
